import {snapshotUnavailableShort} from "../../utils/reportingSnapshotAvailability.js";
import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    KpiCard,
    MetricHelpLabel,
    MetricBasis,
    moneyAmount,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    snapshotCompletenessAlert,
} from "./ReportingLab.jsx";
import {isSnapshotPeriodIncomplete, snapshotDateValue, snapshotHasPublishedDays, snapshotPeriodValue, snapshotRowsForPublishedDays, withSnapshotMissingDays} from "../../utils/reportingSnapshotAvailability.js";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import CentreSpendAttributionNotice, {selectedCentreSpendAttributionStatus} from "./CentreSpendAttributionNotice.jsx";

export function differenceInPercentagePoints(value, comparisonValue) {
    if (!Number.isFinite(value) || !Number.isFinite(comparisonValue)) {
        return null;
    }

    return value - comparisonValue;
}

export function selectedPnoAttributionStatus(data, selectedCentreFilter) {
    return selectedCentreFilter ? selectedCentreSpendAttributionStatus(data?.centreSpendAttribution, true) : null;
}

export function showPnoAttributionNotice(selectedCentreFilter) {
    return selectedCentreFilter === true;
}

export function pnoAttributionValue(value, status) {
    return status === "unknown" ? null : value;
}

export function pnoCompositionAttribution(data) {
    return data?.total?.centreSpendAttribution ?? null;
}

export function settlePnoRequest(request, {isActive, onSuccess, onError, onFinally}) {
    return request
        .then((result) => { if (isActive()) onSuccess(result); })
        .catch((error) => { if (isActive()) onError(error); })
        .finally(() => { if (isActive()) onFinally(); });
}

export function pnoLoadErrorAlert(errorMessage, t) {
    return {
        id: "pno-load-error",
        type: "error",
        showClose: false,
        title: t("google_reporting.load_error_title", {defaultValue: "Část přehledu se nepodařilo načíst"}),
        content: errorMessage || t("google_reporting.load_error", {defaultValue: "Nedostupná data nejsou započítaná jako nula. Zkuste přehled načíst znovu; pokud problém trvá, zkontrolujte stav integrace."}),
    };
}

function pnoValue(entry, metricBasis) {
    return metricBasis === MetricBasis.Completed ? entry?.pnoNet : entry?.pnoGross;
}

function turnoverValue(entry, metricBasis) {
    return metricBasis === MetricBasis.Completed ? entry?.turnoverNet : entry?.turnoverGross;
}

function firstTotalEntry(data) {
    return Object.values(data?.pnoTotalByCurrency ?? {})[0] ?? null;
}

function firstGranularEntries(data) {
    return Object.values(data?.pnoGranularByCurrency ?? {})[0]?.pnoEntries ?? {};
}

function sortedDateKeys(...datasets) {
    return [...new Set(datasets.flatMap((dataset) => Object.keys(dataset ?? {})))]
        .sort((a, b) => a.localeCompare(b));
}

export default function ReportingPnoLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderPercentValue = useRenderPercentValue();
    const metricBasis = MetricBasis.Marketing;
    const totalPnoBasis = metricBasis === MetricBasis.Marketing
        ? t("reporting_lab.pno.total_basis_marketing", {defaultValue: "nestornovaných objednávek"})
        : t("reporting_lab.pno.total_basis_completed", {defaultValue: "dokončených objednávek"});
    const [totalPnoData, setTotalPnoData] = useState(null);
    const [withoutOfflinePnoData, setWithoutOfflinePnoData] = useState(null);
    const [channelsOnlyPnoData, setChannelsOnlyPnoData] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [centreSpendAttribution, setCentreSpendAttribution] = useState(null);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);
    const [loadError, setLoadError] = useState(false);
    const [loadErrorMessage, setLoadErrorMessage] = useState("");
    const metricBasisMeta = useMemo(() => ({
        [MetricBasis.Marketing]: {
            label: t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Marketingový výkon"}),
            shortLabel: t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Bez storen"}),
            description: t("reporting_lab.metric_basis.marketing.description", {defaultValue: "Objednávky, které přišly a nejsou stornované. Vhodné pro vyhodnocení marketingu."}),
        },
        [MetricBasis.Completed]: {
            label: t("reporting_lab.metric_basis.business.label", {defaultValue: "Obchodní výkon"}),
            shortLabel: t("reporting_lab.metric_basis.business.short_label", {defaultValue: "Dokončené"}),
            description: t("reporting_lab.metric_basis.business.description", {defaultValue: "Jen dokončené objednávky. Vhodné pro majitele, finance a reálný vyfakturovaný výkon."}),
        },
    }), [t]);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setLoadError(false);
        setLoadErrorMessage("");
        setTotalPnoData(null);
        setWithoutOfflinePnoData(null);
        setChannelsOnlyPnoData(null);
        setSnapshot(null);
        setCentreSpendAttribution(null);

        const api = new ErpCalculationApi(getAuthorityConfiguration());

        settlePnoRequest(api.reportingPNO({projectId: project.id, erpFilter}), {
            isActive: () => active,
            onSuccess: (nextData) => {
                setTotalPnoData(nextData.total);
                setWithoutOfflinePnoData(nextData.withoutOffline);
                setChannelsOnlyPnoData(nextData.channelsOnly);
                setSnapshot(nextData.snapshot);
                setCentreSpendAttribution(pnoCompositionAttribution(nextData));
            },
            onError: (error) => {
                setLoadError(true);
                et(error).then((message) => { if (active && message) setLoadErrorMessage(message); }).catch(() => {});
            },
            onFinally: () => setLoading(false),
        });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const totalEntry = firstTotalEntry(totalPnoData);
    const withoutOfflineEntry = firstTotalEntry(withoutOfflinePnoData);
    const channelsOnlyEntry = firstTotalEntry(channelsOnlyPnoData);
    const currency = totalEntry?.currency ?? withoutOfflineEntry?.currency ?? channelsOnlyEntry?.currency ?? projectCurrency(project);
    const snapshotIncomplete = isSnapshotPeriodIncomplete(snapshot);
    const summaryUnavailable = !snapshotHasPublishedDays(snapshot);
    const selectedCentreFilter = (centresAllowListed ?? []).length > 0;
    const selectedAttributionStatus = loading ? null : selectedPnoAttributionStatus(totalPnoData, selectedCentreFilter);
    const selectedAttributionIncomplete = selectedAttributionStatus !== null;
    const withoutOfflineAttributionStatus = loading ? null : selectedPnoAttributionStatus(withoutOfflinePnoData, selectedCentreFilter);
    const channelsOnlyAttributionStatus = loading ? null : selectedPnoAttributionStatus(channelsOnlyPnoData, selectedCentreFilter);
    const totalPno = pnoAttributionValue(snapshotPeriodValue(pnoValue(totalEntry, metricBasis), snapshot), selectedAttributionStatus);
    const withoutOfflinePno = pnoAttributionValue(snapshotPeriodValue(pnoValue(withoutOfflineEntry, metricBasis), snapshot), withoutOfflineAttributionStatus);
    const channelsOnlyPno = pnoAttributionValue(snapshotPeriodValue(pnoValue(channelsOnlyEntry, metricBasis), snapshot), channelsOnlyAttributionStatus);
    const offlineImpact = differenceInPercentagePoints(totalPno, withoutOfflinePno);
    const totalGranular = firstGranularEntries(totalPnoData);
    const withoutOfflineGranular = firstGranularEntries(withoutOfflinePnoData);
    const channelsOnlyGranular = firstGranularEntries(channelsOnlyPnoData);
    const dateKeys = withSnapshotMissingDays(snapshot, sortedDateKeys(totalGranular, withoutOfflineGranular, channelsOnlyGranular));
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const pnoTrendOption = {
        color: ["#2563eb", "#16a34a", "#7c3aed"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 30, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, formatChartPercent),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: dateKeys.map((date) => {
                const [, month, day] = date.split("-");
                return `${day}.${month}.`;
            }),
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartPercent},
        },
        series: [
            {
                name: t("reporting_lab.pno.total", {defaultValue: "Celkové PNO"}),
                type: "line",
                smooth: true,
                symbolSize: 6,
                data: dateKeys.map((date) => pnoAttributionValue(snapshotDateValue(pnoValue(totalGranular[date], metricBasis) ?? null, snapshot, date), selectedAttributionStatus)),
            },
            {
                name: t("reporting_lab.pno.without_offline", {defaultValue: "Bez doplňkových zdrojů"}),
                type: "line",
                smooth: true,
                symbolSize: 6,
                data: dateKeys.map((date) => pnoAttributionValue(snapshotDateValue(pnoValue(withoutOfflineGranular[date], metricBasis) ?? null, snapshot, date), withoutOfflineAttributionStatus)),
            },
            {
                name: t("reporting_lab.pno.channels_only", {defaultValue: "Kampaně a další zdroje"}),
                type: "line",
                smooth: true,
                symbolSize: 6,
                data: dateKeys.map((date) => pnoAttributionValue(snapshotDateValue(pnoValue(channelsOnlyGranular[date], metricBasis) ?? null, snapshot, date), channelsOnlyAttributionStatus)),
            },
        ],
    };

    const spendTurnoverOption = {
        color: ["#7c3aed", "#2563eb"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 20, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: dateKeys.map((date) => {
                const [, month, day] = date.split("-");
                return `${day}.${month}.`;
            }),
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartCurrency(value, currency)},
        },
        series: [
            {
                name: t("reporting_lab.pno.marketing_spend", {defaultValue: "Marketingové náklady"}),
                type: "bar",
                data: dateKeys.map((date) => snapshotDateValue(moneyAmount(totalGranular[date]?.spendMarketing) / 100, snapshot, date)),
                barMaxWidth: 24,
            },
            {
                name: t("reporting_lab.table.revenue", {defaultValue: "Obrat"}),
                type: "bar",
                data: dateKeys.map((date) => snapshotDateValue(moneyAmount(turnoverValue(totalGranular[date], metricBasis)) / 100, snapshot, date)),
                barMaxWidth: 24,
            },
        ],
    };

    const selectedAttributionExplanation = selectedAttributionStatus === "incomplete"
        ? t("reporting_lab.centre_spend_attribution.selected", {defaultValue: "Výkon vybraných středisek ukazuje známý přiřazený mezisoučet. Nepřiřazené náklady nelze přiřadit k vybranému středisku, proto jsou jeho souhrnné PNO a zisk neúplné."})
        : selectedAttributionStatus === "unknown"
            ? t("reporting_lab.centre_spend_attribution.unknown_help", {defaultValue: "Odpověď neobsahuje údaje o úplnosti přiřazení ani o částkách. Souhrn vybraných středisek proto nelze ověřit."})
            : null;
    const pnoTooltip = (status, detail = null) => <>
        <div>{t("reporting_lab.metrics_help.marketing_pno", {defaultValue: "PNO = veškeré marketingové náklady za zvolené období a filtry / obrat nestornovaných objednávek × 100%. Náklady se nevylučují podle stavu objednávek."})}</div>
        {detail && <div className="mt-1">{detail}</div>}
        {status === "incomplete" && <div className="mt-1">{t("reporting_lab.centre_spend_attribution.selected", {defaultValue: "Výkon vybraných středisek ukazuje známý přiřazený mezisoučet. Nepřiřazené náklady nelze přiřadit k vybranému středisku, proto jsou jeho souhrnné PNO a zisk neúplné."})}</div>}
        {status === "unknown" && <div className="mt-1">{t("reporting_lab.centre_spend_attribution.unknown_help", {defaultValue: "Odpověď neobsahuje údaje o úplnosti přiřazení ani o částkách. Souhrn vybraných středisek proto nelze ověřit."})}</div>}
    </>;

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.pno", {defaultValue: "PNO a marketingový mix"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[loadError ? pnoLoadErrorAlert(loadErrorMessage, t) : snapshotCompletenessAlert(snapshot, t)]}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.pno.title", {defaultValue: "K čemu slouží PNO a marketingový mix"})}
                >
                    {t("reporting_lab.descriptions.pno.text", {defaultValue: "Ukazuje marketingové náklady jako podíl obratu všech nestornovaných objednávek. Zahrnuje veškeré marketingové náklady ve zvoleném období a filtrech."})}
                </ReportingPageDescription>}>
        {!loadError && <div className="grid grid-cols-12 gap-4">
                <ReportingSnapshotStatus snapshots={snapshot}/>
                {showPnoAttributionNotice(selectedCentreFilter) && <CentreSpendAttributionNotice attribution={centreSpendAttribution} selectedCentreFilter loading={loading}/>}
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.pno.total", {defaultValue: "Celkové PNO"})} value={renderPercentValue(totalPno)} meta={summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : null} helper={t("reporting_lab.pno.total_meta", {basis: totalPnoBasis, defaultValue: `Marketingové náklady / obrat ${totalPnoBasis}.`})} metaTone={summaryUnavailable ? "unavailable" : "default"} info={summaryUnavailable ? t("reporting_lab.snapshot.incomplete_help", {defaultValue: "Souhrny a srovnání za celé období nejsou dostupné. Grafy a dílčí rozklady zahrnují pouze publikované dny."}) : pnoTooltip(selectedAttributionStatus)} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.pno.without_offline", {defaultValue: "Bez doplňkových zdrojů"})} value={renderPercentValue(withoutOfflinePno)} meta={summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : null} helper={t("reporting_lab.pno.without_offline_short", {defaultValue: "Vynechává Mailing, Affiliate a Ostatní."})} info={summaryUnavailable ? t("reporting_lab.snapshot.incomplete_help", {defaultValue: "Souhrny a srovnání za celé období nejsou dostupné. Grafy a dílčí rozklady zahrnují pouze publikované dny."}) : pnoTooltip(withoutOfflineAttributionStatus, t("reporting_lab.pno.without_offline_meta", {defaultValue: "PNO z reklamních nákladů a obratu po vyloučení zdrojů Mailing, Affiliate a Ostatní. Ostatní zdroje včetně kampaní, Direct, Organic a Unknown zůstávají; čitatel i jmenovatel odpovídají tomuto výběru."}))} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.pno.channels_only", {defaultValue: "Kampaně a další zdroje"})} value={renderPercentValue(channelsOnlyPno)} meta={summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : null} helper={t("reporting_lab.pno.channels_only_short", {defaultValue: "Bez Direct, Organic a Unknown."})} info={summaryUnavailable ? t("reporting_lab.snapshot.incomplete_help", {defaultValue: "Souhrny a srovnání za celé období nejsou dostupné. Grafy a dílčí rozklady zahrnují pouze publikované dny."}) : pnoTooltip(channelsOnlyAttributionStatus, t("reporting_lab.pno.channels_only_meta", {defaultValue: "PNO = náklady kampaní a dalších zdrojů / obrat objednávek přiřazených kampaním a těmto zdrojům. Zahrnuje Mailing, Affiliate a Ostatní; Direct, Organic a Unknown jsou vynechány z obou částí."}))} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.pno.offline_impact", {defaultValue: "Rozdíl variant"})} value={summaryUnavailable ? renderPercentValue(null) : offlineImpact === null ? renderPercentValue(null) : `${formatChartNumber(offlineImpact, {maximumFractionDigits: 2})} ${t("reporting_lab.pno.percentage_point_unit", {defaultValue: "p. b."})}`} meta={summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : null} helper={t("reporting_lab.pno.offline_impact_short", {defaultValue: "Celkové PNO − PNO bez doplňkových zdrojů (p. b.)."})} info={summaryUnavailable ? t("reporting_lab.snapshot.incomplete_help", {defaultValue: "Souhrny a srovnání za celé období nejsou dostupné. Grafy a dílčí rozklady zahrnují pouze publikované dny."}) : selectedAttributionIncomplete ? selectedAttributionExplanation : t("reporting_lab.pno.offline_impact_info", {defaultValue: "Rozdíl celkového PNO a PNO bez offline nákladů je uveden v procentních bodech. Varianty mohou mít odlišný obrat i náklady, nejde tedy o samostatně změřený vliv offline nákladů."})} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.pno.trend", {defaultValue: "Vývoj PNO v čase"})} className="col-span-12 xl:col-span-7">
                    <EChart option={pnoTrendOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.pno.spend_vs_revenue", {defaultValue: "Marketingové náklady vs. obrat"})} className="col-span-12 xl:col-span-5">
                    <EChart option={spendTurnoverOption} className="h-96"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.pno.table", {defaultValue: "Rozpad podle měny"})} className="col-span-12">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.pno.currency", {defaultValue: "Měna"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold"><MetricHelpLabel label={t("reporting_lab.pno.total", {defaultValue: "Celkové PNO"})} tooltip={pnoTooltip(selectedAttributionStatus)}/></th>
                                <th className="py-2 pr-4 text-right font-semibold"><MetricHelpLabel label={t("reporting_lab.pno.without_offline", {defaultValue: "Bez doplňkových zdrojů"})} tooltip={pnoTooltip(withoutOfflineAttributionStatus, t("reporting_lab.pno.without_offline_meta", {defaultValue: "PNO z reklamních nákladů a obratu po vyloučení zdrojů Mailing, Affiliate a Ostatní. Ostatní zdroje včetně kampaní, Direct, Organic a Unknown zůstávají; čitatel i jmenovatel odpovídají tomuto výběru."}))}/></th>
                                <th className="py-2 pr-4 text-right font-semibold"><MetricHelpLabel label={t("reporting_lab.pno.channels_only", {defaultValue: "Kampaně a další zdroje"})} tooltip={pnoTooltip(channelsOnlyAttributionStatus, t("reporting_lab.pno.channels_only_meta", {defaultValue: "PNO = náklady kampaní a dalších zdrojů / obrat objednávek přiřazených kampaním a těmto zdrojům. Zahrnuje Mailing, Affiliate a Ostatní; Direct, Organic a Unknown jsou vynechány z obou částí."}))}/></th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.pno.marketing_spend", {defaultValue: "Marketingové náklady"})}</th>
                                <th className="py-2 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {snapshotRowsForPublishedDays(Object.values(totalPnoData?.pnoTotalByCurrency ?? {}), snapshot).map((entry) => {
                                const totalCurrencyEntry = entry;
                                const withoutOfflineCurrencyEntry = Object.values(withoutOfflinePnoData?.pnoTotalByCurrency ?? {}).find((item) => item.currency === entry.currency);
                                const channelsOnlyCurrencyEntry = Object.values(channelsOnlyPnoData?.pnoTotalByCurrency ?? {}).find((item) => item.currency === entry.currency);

                                return <tr key={entry.currency}>
                                    <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{entry.currency}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(pnoAttributionValue(snapshotPeriodValue(pnoValue(totalCurrencyEntry, metricBasis), snapshot), selectedAttributionStatus))}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(pnoAttributionValue(snapshotPeriodValue(pnoValue(withoutOfflineCurrencyEntry, metricBasis), snapshot), withoutOfflineAttributionStatus))}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(pnoAttributionValue(snapshotPeriodValue(pnoValue(channelsOnlyCurrencyEntry, metricBasis), snapshot), channelsOnlyAttributionStatus))}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(snapshotPeriodValue(totalCurrencyEntry?.spendMarketing, snapshot), true)}</td>
                                    <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(snapshotPeriodValue(turnoverValue(totalCurrencyEntry, metricBasis), snapshot), true)}</td>
                                </tr>;
                            })}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>}
    </WideSecureLayout>;
}
